import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useUser } from './UserContext';

const DarkModeContext = createContext();

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

export const DarkModeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useUser();

  // Load initial preference from localStorage
  useEffect(() => {
    const savedPreference = localStorage.getItem('darkMode');
    if (savedPreference !== null) {
      const darkMode = JSON.parse(savedPreference);
      setIsDarkMode(darkMode);
      applyDarkMode(darkMode);
    }
    setIsLoading(false);
  }, []);

  // Apply dark mode to document
  const applyDarkMode = (darkMode) => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Toggle dark mode
  const toggleDarkMode = async () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    applyDarkMode(newDarkMode);
    
    // Save to localStorage
    localStorage.setItem('darkMode', JSON.stringify(newDarkMode));
    
    // Sync to Firebase if user is authenticated
    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          darkMode: newDarkMode
        });
      } catch (error) {
        console.error('Error updating dark mode preference:', error);
      }
    }
  };

  // Listen to Firebase updates when user logs in
  useEffect(() => {
    if (!user || !db) return;

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        if (userData.darkMode !== undefined && userData.darkMode !== isDarkMode) {
          setIsDarkMode(userData.darkMode);
          applyDarkMode(userData.darkMode);
          localStorage.setItem('darkMode', JSON.stringify(userData.darkMode));
        }
      }
    });

    return unsubscribe;
  }, [user, isDarkMode]);

  const value = {
    isDarkMode,
    isLoading,
    toggleDarkMode
  };

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  );
};
